<template>
  <div class="progress-container">
    <b-progress
      class="mb-3"
      :value="donatedAmount > targetAmount ? targetAmount : donatedAmount"
      :max="targetAmount"
    ></b-progress>
    <b-row class="progress-info">
      <b-col class="col-6 progress-amount d-flex flex-column">
        <div class="d-flex align-items-center">
          <span>{{ donatedDisplayAmount }}</span>
          <div id="conversion-tooltip" class="d-flex" tabindex="0">
            <img
              v-if="!isUserCurrencySameAsFundingCurrency"
              class="ml-2 cursor-pointer"
              :src="
                require(`@/assets/icons/funding-${baseCurrency.toLowerCase()}-currency.svg`)
              "
            />
          </div>
        </div>
        <span class="progress-subtext"
          >{{ donationPercentage }}% Pledged of {{ targetDisplayAmount }}</span
        ></b-col
      >
      <b-col class="progress-backers d-flex flex-column align-items-end">
        <span>{{ funding.backersTotal || 0 }}</span>
        <span class="progress-subtext">backers</span></b-col
      >
      <b-col class="progress-days d-flex flex-column align-items-end">
        <span>{{ daysLeft }}</span>
        <span class="progress-subtext">days to go</span></b-col
      >
    </b-row>

    <b-tooltip
      :target="'conversion-tooltip'"
      custom-class="progress-info__tooltip"
      placement="bottom"
    >
      <div class="d-flex flex-column align-items-start">
        <span>Converted from:</span>
        <span
          ><b>{{ donatedDisplayAmountBase }}</b> pledged of
          <b>{{ targetDisplayAmountBase }}</b> goal</span
        >
      </div>
    </b-tooltip>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';

import formatMoney from '@/misc/formatMoney';

export default {
  name: 'FundingProgress',

  props: {
    funding: {
      type: Object,
      default: () => {}
    },
    currency: {
      type: String,
      default: 'USD'
    }
  },

  computed: {
    ...mapGetters({
      region: 'config/region'
    }),
    isUserCurrencySameAsFundingCurrency() {
      return this.funding?.baseCurrency === this.currency;
    },
    baseCurrency() {
      return this.funding.baseCurrency || 'USD';
    },
    donatedAmount() {
      if (this.isUserCurrencySameAsFundingCurrency) {
        return this.funding?.donatedAmount || 0;
      }

      return this.funding.convertedAmounts?.donatedAmount || 0;
    },
    targetAmount() {
      if (this.isUserCurrencySameAsFundingCurrency) {
        return this.funding?.targetAmount || 0;
      }

      return this.funding.convertedAmounts?.targetAmount || 0;
    },
    donationPercentage() {
      return Math.round((this.donatedAmount / this.targetAmount) * 100) || 0;
    },
    targetDisplayAmount() {
      return formatMoney(this.targetAmount, this.currency);
    },
    donatedDisplayAmount() {
      return formatMoney(this.donatedAmount, this.currency);
    },
    targetDisplayAmountBase() {
      return formatMoney(
        this.funding?.targetAmount || 0,
        this.funding?.baseCurrency
      );
    },
    donatedDisplayAmountBase() {
      return formatMoney(
        this.funding?.donatedAmount || 0,
        this.funding?.baseCurrency
      );
    },
    daysLeft() {
      // count "tomorrow" as 1 even if there are only a few hours left
      const days = dayjs(this.funding?.closingDate).diff(dayjs(), 'day') + 1;

      return days > 0 ? days : 0;
    }
  },

  methods: {
    formatMoney
  }
};
</script>

<style lang="scss">
.progress-container {
  .progress {
    background-color: $dark-secondary-color;
    height: 8px;
    display: flex;
    align-items: center;
    border-radius: 10px;
    position: relative;
    overflow: visible;

    .progress-bar {
      background-color: $primary-purple-color;
      height: 16px;
      border-radius: 10px;
      position: absolute;

      @media (min-width: $screen-width-md) {
        height: 24px;
      }
    }
  }

  .progress-amount,
  .progress-backers,
  .progress-days {
    font-weight: bold;
    font-size: 18px;
  }

  .progress-subtext {
    font-size: 12px;
    line-height: 12px;
    color: $grey-color;
  }
}

.progress-info__tooltip {
  font-family: 'Poppins-Regular', sans-serif;
  opacity: 1 !important;

  .arrow {
    display: none;
  }

  .tooltip-inner {
    display: flex;
    align-items: center;
    background: $light-grey-color;
    border-radius: 8px;
    font-size: 14px;
    color: $black-color;
    max-width: 350px;
    height: 60px;
    padding: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    p {
      display: block;
      text-align: left;
    }
  }
}
</style>
